/**
 * Informe Cobertura GSM · lógica pura (sin DOM, sin Vue).
 *
 * Portado del tablero `tablero_cobertura_gsm.html` (v2.5.0) que se usaba
 * pegando a mano el JSON de dbo.Informe_Cobertura_AVL en SSMS. Acá los datos
 * llegan de CLI.GetInformeCoberturaGSM, que ya devuelve todas las unidades
 * juntas y validadas.
 *
 * Criterios (los define el SP, se repiten acá solo para leer el código):
 *  - "sin señal" por punto = la trama llegó bufferada (hora_sistema - hora_avl
 *    mayor al umbral), no en vivo.
 *  - "corte" = par de alertas 107 (pérdida) / 108 (reenganche).
 *  - "sin datos" = tramo sin ningún reporte: no se supone señal ahí.
 */

export const MIN = 60000;
export const DAY = 86400000;

/** Serie por unidad. Sobre fondo claro, slate-700 en lugar de slate-400. */
export const PALETTE = ["#FFA00F", "#6FA3E0", "#2A3145", "#E93241"];

// ---------------------------------------------------------------- formato
// Formato local de datos del manual de marca: 24 hs, coma decimal,
// punto como separador de miles, coordenadas en mono con coma.

export function pad(n) {
  return String(n).padStart(2, "0");
}

export function nf(v, d) {
  if (v == null || isNaN(v)) return "—";
  return Number(v).toLocaleString("es-AR", {
    minimumFractionDigits: d || 0,
    maximumFractionDigits: d || 0,
  });
}

export function pct(v) {
  if (v == null || isNaN(v)) return "—";
  return (
    v.toLocaleString("es-AR", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }) + " %"
  );
}

/** `14:32 · 12/03/2026` */
export function fmtDT(d) {
  if (!d) return "—";
  return `${pad(d.getHours())}:${pad(d.getMinutes())} · ${pad(d.getDate())}/${pad(
    d.getMonth() + 1,
  )}/${d.getFullYear()}`;
}

export function fmtDate(d) {
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
}

export function fmtDur(m) {
  m = Math.round(m);
  if (m < 60) return m + " min";
  if (m < 1440) {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return mm ? `${h} h ${mm} min` : `${h} h`;
  }
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  return h ? `${d} d ${h} h` : `${d} d`;
}

/**
 * Duración en horas y minutos, sin pasar a días: `24 h 45 min`.
 *
 * Para un corte, "1 d" esconde los 45 minutos y dice menos que "24 h 45 min",
 * que es como se venía informando al cliente.
 */
export function fmtDurHm(m) {
  if (m == null || isNaN(m)) return "—";
  m = Math.round(m);
  if (m < 60) return m + " min";
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h} h ${mm} min` : `${h} h`;
}

/** Duración compacta para el centro de la dona de parada: `1,5h` / `53m`. */
export function fmtDwell(min) {
  min = Math.round(min);
  if (min < 60) return min + "m";
  const h = min / 60;
  const v = h < 10 ? Math.round(h * 10) / 10 : Math.round(h);
  return String(v).replace(".", ",") + "h";
}

export function fmtCoord(lat, lon) {
  if (lat == null || lon == null) return "—";
  const f = (v) =>
    Number(v).toLocaleString("es-AR", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });
  return `${f(lat)} · ${f(lon)}`;
}

export function parseDT(s) {
  return s ? new Date(String(s).replace(" ", "T")) : null;
}

export function overlap(a0, a1, b0, b1) {
  return Math.max(0, Math.min(a1, b1) - Math.max(a0, b0));
}

// ---------------------------------------------------------------- parseo

/**
 * Convierte una unidad del JSON del SP en el modelo que usan los gráficos.
 * Mantiene los campos crudos (patente, alias, cliente, chip) para el encabezado.
 */
export function parseUnidad(raw, indice) {
  const desde = parseDT(raw.desde);
  const hasta = parseDT(raw.hasta);

  const cortes = (raw.cortes || [])
    .map((c) => {
      const ini = parseDT(c.inicio);
      const fin = parseDT(c.fin);
      return {
        ini,
        fin,
        t0: ini ? ini.getTime() : NaN,
        t1: fin ? fin.getTime() : NaN,
        dur: (fin - ini) / MIN,
        estado: c.estado,
        lat: c.lat,
        lon: c.lon,
        lugar: c.lugar,
      };
    })
    .filter((c) => c.ini && c.fin && c.t1 >= c.t0);

  const sinDatos = (raw.sin_datos || [])
    .map((g) => ({
      t0: parseDT(g.desde).getTime(),
      t1: parseDT(g.hasta).getTime(),
      tipo: g.tipo,
      minutos: g.minutos,
      // Un corte de conexión no es pérdida de información: el equipo guarda el
      // recorrido y lo manda al reconectarse. `recuperado` dice si eso pasó.
      recuperado: !!g.recuperado,
    }))
    .filter((g) => isFinite(g.t0) && isFinite(g.t1) && g.t1 > g.t0);

  const puntos = (raw.puntos || [])
    .map((p) => ({
      la: +p.la,
      lo: +p.lo,
      s: +p.s,
      g: p.g == null ? null : +p.g,
      b: p.b == null ? 1 : +p.b,
      t: p.t,
      ts: parseDT(p.t).getTime(),
    }))
    .filter((p) => isFinite(p.la) && isFinite(p.lo) && isFinite(p.ts));

  const paradas = (raw.paradas || [])
    .map((p) => ({
      la: +p.la,
      lo: +p.lo,
      ts0: parseDT(p.desde).getTime(),
      ts1: parseDT(p.hasta).getTime(),
      dur: +p.dur_min,
      n: +p.n,
      n_sin: +p.n_sin,
      n_bat: +p.n_bat || 0,
      gsm: p.gsm == null ? null : +p.gsm,
      desde: p.desde,
      hasta: p.hasta,
    }))
    .filter((p) => isFinite(p.la) && isFinite(p.lo) && isFinite(p.ts0));

  return {
    uid: indice,
    hex: PALETTE[indice % PALETTE.length],
    id_avl: raw.id_avl,
    imei: raw.imei,
    patente: raw.patente,
    alias: raw.alias || raw.patente,
    cliente: raw.cliente,
    chipNumero: raw.chip_numero,
    chipEmpresa: raw.chip_empresa,
    equipo: raw.equipo,
    equipoRed: raw.equipo_red,
    conexion: {
      posiciones: raw.posiciones || 0,
      conDemora: raw.con_demora || 0,
      cortes: raw.cortes_conexion || 0,
      masLargoMin: raw.corte_mas_largo_min,
      minSinConexion: raw.min_sin_conexion || 0,
      minVentana: raw.min_ventana || 0,
      sinRecuperar: raw.huecos_sin_recuperar || 0,
    },
    desde,
    hasta,
    t0: desde.getTime(),
    t1: hasta.getTime(),
    primerReporte: parseDT(raw.primer_reporte),
    ultimoReporte: parseDT(raw.ultimo_reporte),
    sin108: raw.cortes_sin_108 || 0,
    gsmMax: raw.gsm_max != null ? +raw.gsm_max : null,
    puntosTotal: raw.puntos_total,
    paradaMin: raw.parada_min,
    cortes,
    sinDatos,
    puntos,
    paradas,
  };
}

// ---------------------------------------------------------------- métricas

/** Cortes por encima del filtro de duración que maneja el usuario. */
export function cortesFiltrados(u, minFiltro) {
  return u.cortes.filter((c) => c.dur > minFiltro);
}

export function metricas(u, minFiltro) {
  const cs = cortesFiltrados(u, minFiltro);
  const ventana = (u.t1 - u.t0) / MIN;
  let total = 0;
  let max = 0;
  let suma = 0;
  cs.forEach((c) => {
    total += overlap(c.t0, c.t1, u.t0, u.t1) / MIN;
    if (c.dur > max) max = c.dur;
    suma += c.dur;
  });
  return {
    n: cs.length,
    totalMin: total,
    pct: ventana > 0 ? (total / ventana) * 100 : 0,
    avg: cs.length ? suma / cs.length : 0,
    max,
  };
}

/**
 * % de cada día con reporte. Los tramos sin datos no cuentan como caída:
 * se descuentan del día, y un día entero sin datos no dibuja barra.
 */
export function coberturaDiaria(u, minFiltro) {
  const out = [];
  const inicio = new Date(u.desde);
  inicio.setHours(0, 0, 0, 0);
  const cs = cortesFiltrados(u, minFiltro);
  const noData = (u.sinDatos || []).filter((g) => g.tipo === "sin_datos");

  for (let d = inicio.getTime(); d < u.t1; d += DAY) {
    const ds = Math.max(d, u.t0);
    const de = Math.min(d + DAY, u.t1);
    const disponible = (de - ds) / MIN;
    if (disponible <= 0) continue;

    let sinInfo = 0;
    noData.forEach((g) => {
      sinInfo += overlap(g.t0, g.t1, ds, de) / MIN;
    });
    sinInfo = Math.min(sinInfo, disponible);

    const conDatos = disponible - sinInfo;
    if (conDatos <= 1) {
      out.push({ t: d, up: null });
      continue;
    }

    let caido = 0;
    cs.forEach((c) => {
      caido += overlap(c.t0, c.t1, ds, de) / MIN;
    });
    caido = Math.min(caido, conDatos);
    out.push({ t: d, up: ((conDatos - caido) / conDatos) * 100 });
  }
  return out;
}

export const BINS = [
  [20, 30, "20–30 min"],
  [30, 60, "30–60 min"],
  [60, 120, "1–2 h"],
  [120, 240, "2–4 h"],
  [240, 1e9, "4 h +"],
];

export function histograma(u, minFiltro) {
  const cs = cortesFiltrados(u, minFiltro);
  return BINS.map((b) => cs.filter((c) => c.dur >= b[0] && c.dur < b[1]).length);
}

export function porHora(u, minFiltro) {
  const cs = cortesFiltrados(u, minFiltro);
  const a = new Array(24).fill(0);
  cs.forEach((c) => a[c.ini.getHours()]++);
  return a;
}

/**
 * Métricas de conexión de una unidad, las que van en la tarjeta.
 *
 * Ojo con la diferencia, porque son dos cosas distintas:
 *  - "corte de conexión" = el equipo pasó más de @GapMin minutos sin transmitir
 *    (reporta cada 8 como máximo). Es lo que se le informa al cliente.
 *  - "corte de señal"     = par de alertas 107/108, lo que muestran los gráficos
 *    y la tabla de detalle.
 */
export function conexion(u) {
  const c = u.conexion || {};
  const ventana = c.minVentana || (u.t1 - u.t0) / MIN;
  const enVivo = ventana > 0 ? ((ventana - c.minSinConexion) / ventana) * 100 : null;
  return {
    enVivoPct: enVivo,
    cortes: c.cortes,
    masLargoMin: c.masLargoMin,
    posiciones: c.posiciones,
    conDemoraPct: c.posiciones > 0 ? (c.conDemora / c.posiciones) * 100 : 0,
    sinRecuperar: c.sinRecuperar,
    integro: c.sinRecuperar === 0,
  };
}

/** Color por señal GSM (0..max): rojo (débil) → naranja → verde (fuerte). */
export function gsmColor(g, gmax) {
  if (g == null) return "#9AA0AE";
  const m = gmax && gmax > 0 ? gmax : 5;
  const t = Math.max(0, Math.min(1, g / m));
  const stops = [
    [233, 50, 65],
    [255, 160, 15],
    [111, 192, 111],
  ];
  const i = t < 0.5 ? 0 : 1;
  const u = t < 0.5 ? t / 0.5 : (t - 0.5) / 0.5;
  const a = stops[i];
  const b = stops[i + 1];
  return `rgb(${Math.round(a[0] + (b[0] - a[0]) * u)},${Math.round(
    a[1] + (b[1] - a[1]) * u,
  )},${Math.round(a[2] + (b[2] - a[2]) * u)})`;
}

/**
 * `datetime-local` (YYYY-MM-DDTHH:mm) -> literal para el SP.
 *
 * Se manda en ISO 8601 con la T y los segundos: ese formato SQL Server lo
 * interpreta siempre igual, sin importar el DATEFORMAT ni el idioma de la
 * sesión. Con "2026-09-05 00:00" (sin T), una sesión en dmy puede leer el 5 de
 * septiembre como el 9 de mayo.
 */
export function aParametroSql(valor) {
  if (!valor) return null;
  const v = String(valor);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v)) return v + ":00";
  return v;
}
